import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Slider,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ContactsIcon from '@mui/icons-material/Contacts';
import ImageIcon from '@mui/icons-material/Image';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import LaunchIcon from '@mui/icons-material/Launch';
import WarningIcon from '@mui/icons-material/Warning';
import helpers from 'src/helpers';

// Helper to categorize file types for display
const getFileTypeCategory = (fileName, contentType = '') => {
  try {
    // Prefer the MIME content type when available (Telnyx provides it on the
    // webhook payload). Falls through to extension logic when absent or for
    // categories not derivable from the MIME top-level type.
    if (contentType) {
      if (contentType.startsWith('image/')) return 'image';
      if (contentType.startsWith('video/')) return 'video';
      if (contentType.startsWith('audio/')) return 'audio';
    }

    if (!fileName) {
      return 'unknown';
    }

    const extension = fileName.toLowerCase().split('.').pop();

    const categories = {
      image: ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'],
      document: ['pdf', 'doc', 'docx', 'txt', 'rtf'],
      video: ['mp4', '3gp', 'mov', 'avi', 'webm', 'mkv', 'wmv', 'flv'],
      audio: ['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac', 'wma', 'amr'],
      contact: ['vcf'],
      archive: ['zip', 'rar', '7z']
    };

    if (categories.image.includes(extension)) return 'image';
    if (categories.document.includes(extension)) return 'document';
    if (categories.video.includes(extension)) return 'video';
    if (categories.audio.includes(extension)) return 'audio';
    if (categories.contact.includes(extension)) return 'contact';
    if (categories.archive.includes(extension)) return 'archive';

    return 'unknown';
  } catch (e) {
    return 'unknown';
  }
};

// Get display information for different file types
const getFileDisplayInfo = (fileName, contentType = '') => {
  try {
    const category = getFileTypeCategory(fileName, contentType);
    const extension = fileName.toLowerCase().split('.').pop();

    const displayInfo = {
      image: {
        canPreview: true,
        icon: ImageIcon,
        description: `Image ${extension.toUpperCase()}`,
        color: '#4CAF50'
      },
      document: {
        canPreview: extension === 'pdf',
        icon: extension === 'pdf' ? PictureAsPdfIcon : DescriptionIcon,
        description: extension === 'pdf' ? 'PDF Document' : `Document ${extension.toUpperCase()}`,
        color: '#F44336'
      },
      video: {
        canPreview: true,
        icon: VideoFileIcon,
        description: `Video ${extension.toUpperCase()}`,
        color: '#2196F3'
      },
      audio: {
        canPreview: true,
        icon: VolumeUpIcon,
        description: `Audio ${extension.toUpperCase()}`,
        color: '#FF9800'
      },
      contact: {
        canPreview: false,
        icon: ContactsIcon,
        description: `Contact Card ${extension.toUpperCase()}`,
        color: '#9C27B0'
      },
      archive: {
        canPreview: false,
        icon: DescriptionIcon,
        description: `Archive ${extension.toUpperCase()}`,
        color: '#607D8B'
      },
      unknown: {
        canPreview: false,
        icon: DescriptionIcon,
        description: `File ${extension.toUpperCase()}`,
        color: '#757575'
      }
    };

    return displayInfo[category] || displayInfo.unknown;
  } catch (e) {
    return {
      canPreview: false,
      icon: DescriptionIcon,
      description: 'File',
      color: '#757575'
    };
  }
};

// Format file size helper - ESLint compliant
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / (k ** i)).toFixed(1))} ${sizes[i]}`;
};

// CON-1674 — m:ss for the playback scrubber. Always returns a real clock
// value; the "we do not know yet" case is the TOTAL, and the caller decides
// how to render that, because 0:00 for an unknown duration would read as a
// zero-length file rather than as a number that has not arrived.
const formatPlaybackTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

// CON-1674 — which extensions a browser can actually decode.
//
// This was a DENYLIST of ['amr', '3gp'], which meant every other extension
// got a play button whether or not a browser could play it. `.wma` is
// accepted by the upload picker (NewMessage/index.jsx) and plays in no
// browser at all, so that button already existed and silently did nothing.
// An allowlist fails the other way: an unknown format falls back to the
// download card, which works.
//
// Deliberately excluded, and each for a reason rather than by omission:
//   amr, awb  no browser decodes AMR. These are what the transcode exists
//             for — an AMR with a playbackUrl plays via the derived MP3.
//   wma       no browser decodes it, on any platform.
//   3gp       reaches here only if categorised as audio; the container is
//             video and the codec is usually H.263 + AMR.
const BROWSER_PLAYABLE_AUDIO = ['mp3', 'wav', 'm4a', 'mp4', 'aac', 'flac', 'ogg', 'oga', 'opus', 'weba'];

const isAudioFormatSupported = (fileName) => {
  if (!fileName) return false;

  const extension = fileName.toLowerCase().split('.').pop();

  return BROWSER_PLAYABLE_AUDIO.includes(extension);
};

function AttachmentRenderer({
  fileName,
  url,
  isSingle = false,
  // onPreview = () => { },
  isExternal = false,
  fileSize = 0,
  expiryWarning = false,
  size,
  contentType = '',
  playbackUrl = '',
}) {
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState(null);
  // CON-1674 — scrubber state. Both stay at 0 until the first press, because
  // nothing is loaded before then; see handleAudioToggle.
  const [playbackPosition, setPlaybackPosition] = useState(0);
  const [playbackDuration, setPlaybackDuration] = useState(0);

  // An Audio object is not part of the React tree, so unmounting the message
  // does not stop it. Without this, scrolling a playing memo out of a long
  // conversation leaves it playing with no way to reach the pause button.
  useEffect(() => () => {
    if (audioElement) {
      audioElement.pause();
    }
  }, [audioElement]);

  // Get clean display name for user interface
  // const displayFileName = getDisplayFileName(fileName);
  const displayFileName = helpers.azure.getDisplayFileName(fileName);

  const fileInfo = getFileDisplayInfo(displayFileName, contentType);
  const category = getFileTypeCategory(displayFileName, contentType);
  const IconComponent = fileInfo.icon;
  const fileSizeText = formatFileSize(fileSize);

  // CON-1674 — the total duration is only knowable once the browser has read
  // the file header. We deliberately do NOT preload it: a conversation can
  // hold a dozen attachments, and fetching a header for each one on open
  // would cost a request per card to display a number nobody is looking at
  // yet. duration therefore arrives with the first press and not before.
  //
  // Infinity is a real answer here for a stream whose length the browser
  // cannot determine. Storing 0 in that case keeps the placeholder showing
  // rather than printing a nonsense total.
  const readDuration = (audio) => {
    setPlaybackDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
  };

  const handleAudioToggle = () => {
    if (!audioElement) {
      // CON-1674 — the derived MP3 when one exists, the original otherwise.
      // Only playback is redirected: the filename shown, the card click and
      // the download button all still refer to the original file, which is
      // what the contact actually sent.
      const audio = new Audio(playbackUrl || url);
      audio.onloadedmetadata = () => readDuration(audio);
      // Some browsers settle the duration only after metadata, on a second
      // event; reading it twice is cheap and missing it leaves a permanent
      // placeholder.
      audio.ondurationchange = () => readDuration(audio);
      audio.ontimeupdate = () => setPlaybackPosition(audio.currentTime);
      audio.onended = () => {
        setIsAudioPlaying(false);
        // Rewind, so a second press replays rather than sitting at the end
        // with a full slider and nothing happening.
        audio.currentTime = 0;
        setPlaybackPosition(0);
      };
      setAudioElement(audio);
      audio.play();
      setIsAudioPlaying(true);
    } else if (isAudioPlaying) {
      audioElement.pause();
      setIsAudioPlaying(false);
    } else {
      audioElement.play();
      setIsAudioPlaying(true);
    }
  };

  // CON-1674 — seeking. Setting currentTime is what actually moves playback;
  // setPlaybackPosition is only so the thumb tracks the drag rather than
  // snapping back while waiting for the next timeupdate.
  const handleSeek = (event, value) => {
    if (!audioElement) return;
    audioElement.currentTime = value;
    setPlaybackPosition(value);
  };

  const handleDownload = () => {
    if (isExternal) {
      window.open(url, '_blank');
      return;
    }

    const link = document.createElement('a');
    link.href = url;
    link.download = helpers.azure.getDisplayFileName(fileName); // Use clean filename for download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExternalFileClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    window.open(url, '_blank');
  };

  // Image files - handle external vs stored
  if (category === 'image') {
    if (isExternal) {
      return (
        <Box
          sx={{
            cursor: 'pointer',
            position: 'relative',
            ...(size
              ? { width: size, height: size }
              : isSingle
                ? { maxWidth: '100%', height: '200px' }
                : { width: '100px', height: '100px' }
            )
          }}
          onClick={(e) => handleExternalFileClick(e)}
        >
          <img
            src={url}
            alt="Attachment"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '4px'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
              e.target.parentElement.style.pointerEvents = 'none';
            }}
          />
          <Box
            sx={{
              display: 'none',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              backgroundColor: '#f0f0f0',
              borderRadius: '4px',
              pointerEvents: 'none',
              px: 2
            }}
          >
            <Typography variant="body2" color="text.secondary" textAlign="center">
              This attachment
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              can't be displayed
            </Typography>
          </Box>
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              backgroundColor: 'rgba(0,0,0,0.7)',
              borderRadius: '50%',
              padding: '2px',
            }}
          >
            <LaunchIcon sx={{ color: 'white', fontSize: '12px' }} />
          </Box>
          {expiryWarning && (
            <Chip
              size="small"
              icon={<WarningIcon />}
              label="Link may expire"
              sx={{
                position: 'absolute',
                bottom: 4,
                left: 4,
                backgroundColor: 'rgba(255,193,7,0.9)',
                color: 'black',
                fontSize: '10px',
                height: '20px'
              }}
            />
          )}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          cursor: 'pointer',
          ...(size
            ? { width: size, height: size }
            : isSingle
              ? { maxWidth: '100%', height: '200px' }
              : { width: '100px', height: '100px' }
          )
        }}
        onClick={(e) => handleExternalFileClick(e)}
      >
        <img
          src={url}
          alt="Attachment"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '4px'
          }}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
            e.target.parentElement.style.pointerEvents = 'none';
          }}
        />
        <Box
          sx={{
            display: 'none',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            backgroundColor: '#f0f0f0',
            borderRadius: '4px',
            pointerEvents: 'none',
            px: 2
          }}
        >
          <Typography variant="body2" color="text.secondary" textAlign="center">
            This attachment
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            can't be displayed
          </Typography>
        </Box>
      </Box>
    );
  }

  // PDF files - handle external vs stored
  if (category === 'document' && displayFileName.toLowerCase().endsWith('.pdf')) {
    return (
      <Card
        sx={{
          ...(size
            ? { width: size, height: size }
            : isSingle ? { maxWidth: '300px' } : { width: '200px' }
          ),
          cursor: 'pointer',
          '&:hover': { boxShadow: 3 },
          ...(isExternal ? { border: '1px solid #e0e0e0' } : {})
        }}
        onClick={(e) => handleExternalFileClick(e)}
      >
        <CardContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            p: 2,
            height: '100%'
          }}
        >
          <IconComponent sx={{ color: fileInfo.color, fontSize: '24px' }} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {displayFileName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {fileInfo.description}
              {fileSizeText && ` • ${fileSizeText}`}
              {isExternal && ' • Opens in browser'}
            </Typography>
            {expiryWarning && (
              <Typography variant="caption" color="warning.main" display="block">
                ⚠️ Link may expire
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDownload();
            }}
          >
            {isExternal ? <LaunchIcon fontSize="small" /> : <DownloadIcon fontSize="small" />}
          </IconButton>
        </CardContent>
      </Card>
    );
  }

  // Video files - handle external vs stored
  if (category === 'video') {
    if (isExternal) {
      return (
        <Card
          sx={{
            ...(size
              ? { width: size, height: size }
              : isSingle ? { maxWidth: '300px' } : { width: '200px' }
            ),
            cursor: 'pointer',
            border: '1px solid #e0e0e0',
            '&:hover': { boxShadow: 3 }
          }}
          onClick={(e) => handleExternalFileClick(e)}
        >
          <CardContent
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              p: 2,
              height: '100%'
            }}
          >
            <IconComponent sx={{ color: fileInfo.color, fontSize: '24px' }} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {displayFileName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {fileInfo.description}
                {fileSizeText && ` • ${fileSizeText}`}
                {' • Opens in browser'}
              </Typography>
              {expiryWarning && (
                <Typography variant="caption" color="warning.main" display="block">
                  ⚠️ Link may expire
                </Typography>
              )}
            </Box>
            <LaunchIcon fontSize="small" />
          </CardContent>
        </Card>
      );
    }

    return (
      <Box
        sx={{
          ...(size
            ? { width: size, height: size }
            : isSingle
              ? { maxWidth: '100%', height: '200px' }
              : { width: '200px', height: '150px' }
          ),
          cursor: 'pointer',
          borderRadius: '4px',
          overflow: 'hidden',
          position: 'relative',
        }}
        onClick={(e) => handleExternalFileClick(e)}
      >
        <video
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
          controls
          preload="metadata"
        >
          <source src={url} />
          <track kind="captions" srcLang="en" label="English" />
          Your browser does not support the video tag.
        </video>
        {/* Video format notice */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 4,
            left: 4,
            right: 4,
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: 'white',
            fontSize: '11px',
            padding: '4px 6px',
            borderRadius: '3px',
            textAlign: 'center'
          }}
        >
          Click to open in new tab if video doesn't play
        </Box>
      </Box>
    );
  }

  // Audio files - Clean handling without browser support warnings
  if (category === 'audio') {
    // A recorded playbackUrl is a promise from the server that a playable
    // copy exists — it is written only after a successful upload. So it
    // overrides the extension check rather than being checked alongside it.
    // No playbackUrl and an undecodable extension gives exactly the card
    // this shipped with: Launch icon, disabled play, working download.
    const isSupported = isAudioFormatSupported(displayFileName) || Boolean(playbackUrl);

    // The scrubber appears ONLY where playback can actually happen. The
    // fallback card does not get a greyed-out slider: an inert control that
    // looks like a control is the same failure that was just removed from
    // isAudioFormatSupported, where a play button appeared for formats no
    // browser can decode. External attachments are excluded for the same
    // reason — their play button is disabled too.
    const showScrubber = isSupported && !isExternal;

    return (
      <Card
        sx={{
          ...(size
            ? { width: size, height: size }
            : isSingle ? { maxWidth: '300px' } : { width: '200px' }
          ),
          '&:hover': { boxShadow: 3 },
          ...(isExternal ? { border: '1px solid #e0e0e0' } : {}),
          cursor: 'pointer'
        }}
        onClick={(e) => handleExternalFileClick(e)}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 1,
            p: 2,
            height: '100%'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              width: '100%'
            }}
          >
            <IconButton
              onClick={(e) => {
                if (isExternal || !isSupported) {
                // Don't stop propagation - let card click handle it
                  return;
                }
                e.stopPropagation();
                handleAudioToggle();
              }}
              disabled={isExternal || !isSupported}
            >
              {isExternal || !isSupported ? (
                <LaunchIcon />
              ) : (
                isAudioPlaying ? <PauseIcon /> : <PlayArrowIcon />
              )}
            </IconButton>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {displayFileName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {fileInfo.description}
                {fileSizeText && ` • ${fileSizeText}`}
                {isExternal && ' • Opens in browser'}
              </Typography>
              {expiryWarning && (
              <Typography variant="caption" color="warning.main" display="block">
                ⚠️ Link may expire
              </Typography>
              )}
            </Box>
            {!isExternal && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDownload();
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
            )}
          </Box>
          {showScrubber && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%'
              }}
              // The Card itself opens the file in a new tab on click, so
              // without this a drag of the thumb also navigates away. Mouse
              // down is stopped as well: a drag released outside the slider
              // still delivers a click to the card.
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ minWidth: '32px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
              >
                {formatPlaybackTime(playbackPosition)}
              </Typography>
              <Slider
                size="small"
                aria-label={`Seek ${displayFileName}`}
                value={Math.min(playbackPosition, playbackDuration || 0)}
                max={playbackDuration || 1}
                step={0.1}
                // Nothing is loaded until the first press, so there is
                // nothing to seek within. Disabled reads as "not yet",
                // which is true, rather than as "broken".
                disabled={!audioElement}
                onChange={handleSeek}
                sx={{ flexGrow: 1, py: 1 }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ minWidth: '32px', fontVariantNumeric: 'tabular-nums' }}
              >
                {playbackDuration ? formatPlaybackTime(playbackDuration) : '--:--'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  // All other file types (including VCF, TXT, etc.)
  return (
    <Card
      sx={{
        ...(size
          ? { width: size, height: size }
          : isSingle ? { maxWidth: '300px' } : { width: '200px' }
        ),
        cursor: 'pointer',
        '&:hover': { boxShadow: 3 },
        ...(isExternal ? { border: '1px solid #e0e0e0' } : {})
      }}
      onClick={(e) => handleExternalFileClick(e)}
    >
      <CardContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          p: 2,
          height: '100%'
        }}
      >
        <IconComponent sx={{ color: fileInfo.color, fontSize: '24px' }} />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap>
            {displayFileName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {fileInfo.description}
            {fileSizeText && ` • ${fileSizeText}`}
            {isExternal && ' • Opens in browser'}
          </Typography>
          {expiryWarning && (
            <Typography variant="caption" color="warning.main" display="block">
              ⚠️ Link may expire
            </Typography>
          )}
        </Box>
        <IconButton size="small">
          {isExternal ? <LaunchIcon fontSize="small" /> : <DownloadIcon fontSize="small" />}
        </IconButton>
      </CardContent>
    </Card>
  );
}

AttachmentRenderer.propTypes = {
  fileName: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  isSingle: PropTypes.bool,
  // onPreview: PropTypes.func,
  isExternal: PropTypes.bool,
  fileSize: PropTypes.number,
  expiryWarning: PropTypes.bool,
  size: PropTypes.number,
  contentType: PropTypes.string,
  playbackUrl: PropTypes.string,
};

export default AttachmentRenderer;
